import {
	getItemPreviewText,
	getItemQuestionType,
	getItemScorePercentage,
	getItemScoreText,
	getItemSkill,
	hasItemScoreData,
	isItemAnsweredCorrectly,
	isItemMarked,
	isItemSkipped,
} from "@memorang/helpers/item";
import type { BaseItemSchema, SkillTypes } from "@memorang/types";
import { useTranslation } from "react-i18next";
import { List } from "react-native-paper";
import { questionTypeIconMap, skillIconMap } from "../../constants";
import { useAppConfig } from "../../contexts/AppConfigContext";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import CustomChip from "../CustomChip";
import { ScoreChip } from "../ScoreChip";
import TooltipWrapper from "../TooltipWrapper";

type Props = {
	item: BaseItemSchema;
	isMarkable?: boolean;
	hideSkills?: boolean;
	hideDivider?: boolean;
};

const CorrectnessIcon = ({
	isCorrect,
	isSkipped,
}: {
	isCorrect: boolean;
	isSkipped: boolean;
}) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const {
		name,
		color,
		label,
	}: { name: IconType; color: string; label: string } = isSkipped
		? {
				name: "minus-circle-outline",
				color: theme.colors.onSurfaceVariant,
				label: t("common.skipped"),
			}
		: isCorrect
			? {
					name: "check-circle-outline",
					color: theme.palette.success.main,
					label: t("common.correct"),
				}
			: {
					name: "close-circle-outline",
					color: theme.colors.error,
					label: t("common.incorrect"),
				};

	return (
		<TooltipWrapper title={label}>
			<AppIcon name={name} size={20} color={color} accessibilityLabel={label} />
		</TooltipWrapper>
	);
};

export const SessionReportAnswerRow = ({
	item,
	isMarkable = false,
	hideSkills = false,
	hideDivider = false,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { allowMarking = true } = useAppConfig();

	const skill = getItemSkill(item);
	const questionType = getItemQuestionType(item);
	const previewText = getItemPreviewText(item);
	const scoreText = getItemScoreText(item);
	const scorePercentage = getItemScorePercentage(item);
	const isCorrect = isItemAnsweredCorrectly(item);
	const isSkipped = isItemSkipped(item);
	const isMarked = isItemMarked(item);
	const showScoreChip = hasItemScoreData(item);

	return (
		<List.Item
			style={{
				borderBottomWidth: hideDivider ? 0 : 1,
				borderBottomColor: theme.colors.surfaceVariant,
				paddingLeft: 0,
				paddingRight: 0,
				paddingVertical: 12,
			}}
			contentStyle={{ paddingLeft: 0, paddingRight: 8, gap: 6 }}
			title={() => (
				<Box flexDirection="row" alignItems="center" gap={8} minWidth={0}>
					{skill && !hideSkills ? (
						<TooltipWrapper title={skill.title}>
							<Box
								width={30}
								height={30}
								alignItems="center"
								justifyContent="center"
								backgroundColor={theme.colors.secondaryContainer}
								borderRadius={8}
							>
								<AppIcon
									name={skillIconMap[skill.title]}
									size={18}
									color={theme.colors.onSecondaryContainer}
								/>
							</Box>
						</TooltipWrapper>
					) : null}
					{questionType ? (
						<Box flexShrink={1} minWidth={0}>
							<CustomChip
								icon={questionTypeIconMap[questionType.title]}
								iconColor={theme.colors.onSecondaryContainer}
								numberOfLines={1}
								textStyle={{ flexShrink: 1 }}
							>
								{questionType.title}
							</CustomChip>
						</Box>
					) : null}
				</Box>
			)}
			description={previewText}
			descriptionStyle={{ fontSize: 12 }}
			descriptionNumberOfLines={1}
			// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
			right={() => (
				<Box flexDirection="row" alignItems="center" gap={8} alignSelf="center">
					{(item.metadata.skill as SkillTypes | "UNSCORED") !== "UNSCORED" ? (
						showScoreChip ? (
							<ScoreChip
								isCorrect={isCorrect}
								isSkipped={isSkipped}
								scorePercentage={scorePercentage}
								value={scoreText}
							/>
						) : (
							<CorrectnessIcon isCorrect={isCorrect} isSkipped={isSkipped} />
						)
					) : null}
					{isMarkable && allowMarking ? (
						<TooltipWrapper
							title={isMarked ? t("common.marked") : t("common.unmarked")}
						>
							<AppIcon
								name={isMarked ? "flag" : "flag-outline"}
								size={16}
								color={
									isMarked
										? theme.colors.primary
										: theme.colors.onSurfaceVariant
								}
							/>
						</TooltipWrapper>
					) : null}
				</Box>
			)}
		/>
	);
};
